<template>
    <page :tabs="tabs" :busy="busy" :compact-mobile-view="true" :compact-mobile-tabs="1">
        <div class="d-flex flex-column h-100">
            <b-button-toolbar class="m-1 ml-2">
                <responsive-button
                    :icon="['far', 'file-pdf']"
                    variant="primary"
                    @click="handleDownloadPDF"
                    :disabled="!currentPreview"
                    :busy="pdfBusy"
                    >Download PDF</responsive-button
                >
                <responsive-button
                    :icon="['fas', 'file-excel']"
                    variant="primary"
                    @click="handleDownloadExcel"
                    :disabled="!currentPreview"
                    :busy="excelBusy"
                    >Download Excel</responsive-button
                >
                <responsive-button
                    v-if="histories.length > 1"
                    :icon="['fas', 'clock-rotate-left']"
                    variant="primary"
                    @click="() => $bvModal.show(historyModalId)"
                    :disabled="!currentPreview"
                    >History</responsive-button
                >
                <responsive-button
                    :icon="['fas', isPublished ? 'xmark' : 'check']"
                    variant="primary"
                    @click="() => $bvModal.show(publishModalId)"
                    :disabled="!isViewingLatestHistory"
                >
                    {{ isPublished ? 'Unpublish' : 'Publish' }}
                </responsive-button>
            </b-button-toolbar>
            <div class="position-relative flex-grow-1">
                <div class="spinner-wrapper" v-if="!busy && !previewLoaded">
                    <b-spinner variant="primary" class="spinner"></b-spinner>
                </div>
                <iframe
                    :key="historyId"
                    v-if="currentPreview"
                    :src="pdfUrl"
                    class="border-0 position-absolute w-100 h-100"
                    @load="handlePreviewLoaded"
                ></iframe>
            </div>
        </div>
        <owner-statement-publish-unpublish-modal
            :modal-id="publishModalId"
            :action="isPublished ? 'unpublish' : 'publish'"
            :owners-data="[{ statementId: this.ownerStatementId }]"
            :is-bulk-action="false"
            :is-single-owner-selected="true"
            :loading="busy"
            @ok="publishUnpublish"
        />
        <history-modal :modal-id="historyModalId" :histories="histories"></history-modal>
    </page>
</template>

<script>
import API from '../../api';
import Page from '../generic/page/page.vue';
import HistoryModal from './history-modal.vue';
import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
import OwnerStatementPublishUnpublishModal from './owner-statement-publish-unpublish-modal.vue';
import {
    OWNER_STATEMENT_ENDPOINT,
    OWNER_STATEMENT_GENERATE_PREVIEW_ENDPOINT,
    OWNER_STATEMENT_STATUS_HISTORY_ENDPOINT,
    OWNER_STATEMENT_STATUS_HISTORY_PDF_ENDPOINT,
    OWNER_STATEMENT_STATUS_HISTORY_EXCEL_ENDPOINT,
} from '../../endpoints';
import { convertBlobResponseToJson, getFilenameFromResponse, formatResponseError } from '../../utils/api';
import { saveAs } from 'file-saver';
export default {
    name: 'OwnerStatementPreview',
    components: {
        Page,
        ResponsiveButton,
        OwnerStatementPublishUnpublishModal,
        HistoryModal,
    },
    data() {
        return {
            busy: false,
            publishModalId: 'modal-publish',
            historyModalId: 'history-modal',
            previewLoaded: false,
            histories: [],
            pdfBusy: false,
            excelBusy: false,
        };
    },
    computed: {
        tabs() {
            return [
                { title: 'Generation', active: false, route: '/app/owner-statement/list' },
                { title: `Statement #${this.$route.params.id}`, active: true },
            ];
        },
        ownerStatementId() {
            return this.$route.params.id;
        },
        historyId() {
            return this.$route.query.history_id || this.histories[0]?.id;
        },
        pdfUrl() {
            return OWNER_STATEMENT_STATUS_HISTORY_PDF_ENDPOINT(this.ownerStatementId, this.historyId);
        },
        excelUrl() {
            return OWNER_STATEMENT_STATUS_HISTORY_EXCEL_ENDPOINT(this.ownerStatementId, this.historyId);
        },
        currentPreview() {
            return this.histories.find((h) => h.id == this.historyId);
        },
        isViewingLatestHistory() {
            return this.histories.length > 0 && this.historyId == this.histories[0]?.id;
        },
        isPublished() {
            return this.histories.length > 0 && this.histories[0]?.status === 'Published';
        }
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            this.busy = true;
            try {
                await this.generatePreview();
                await this.fetchHistory();
            } catch {
            } finally {
                this.busy = false;
            }
        },
        async generatePreview() {
            try {
                await API.post(OWNER_STATEMENT_GENERATE_PREVIEW_ENDPOINT(this.ownerStatementId));
            } catch (e) {
                this.errorToast({ text: 'Could not generate owner statement preview.' });
                throw e;
            }
        },
        async fetchHistory() {
            try {
                const { data } = await API.get(OWNER_STATEMENT_STATUS_HISTORY_ENDPOINT(this.ownerStatementId));
                this.histories = data;
            } catch (e) {
                this.errorToast({ text: 'Could not load owner statement history.' });
                throw e;
            }
        },
        handlePreviewLoaded() {
            this.previewLoaded = true;
        },
        async handleDownloadPDF() {
            try {
                this.pdfBusy = true;
                const response = await API.get(this.pdfUrl, { responseType: 'blob' });
                saveAs(response.data, getFilenameFromResponse(response));
            } catch (e) {
                if (e.response) {
                    e.response.data = await convertBlobResponseToJson(e.response);
                }
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not download owner statement pdf.' });
            } finally {
                this.pdfBusy = false;
            }
        },
        async handleDownloadExcel() {
            try {
                this.excelBusy = true;
                const response = await API.get(this.excelUrl, { responseType: 'blob' });
                saveAs(response.data, getFilenameFromResponse(response));
            } catch (e) {
                if (e.response) {
                    e.response.data = await convertBlobResponseToJson(e.response);
                }
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not download owner statement excel.' });
            } finally {
                this.excelBusy = false;
            }
        },
        async publishUnpublish(params) {
            this.busy = true;
            try {
                const payload = this.isPublished
                    ? {}
                    : {
                            save_to_owner_account: params.saveToOwnerAccount,
                            send_email_to_owner: params.sendEmailToOwner,
                            email_data: params.emailData,
                        };

                await API.post(
                    OWNER_STATEMENT_ENDPOINT(this.ownerStatementId) +
                        `${this.isPublished ? 'unpublish/' : 'publish/'}`,
                    payload
                );
                await this.fetchData();
                this.$router.push(`/app/owner-statement/list/${this.ownerStatementId}/preview`);
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.errorToast({
                    text: generic || `Could not ${this.isPublished ? 'unpublish' : 'publish'} owner statement.`,
                });
            } finally {
                this.busy = false;
                this.$bvModal.hide(this.publishModalId);
            }
        },
    },
    watch: {
        historyId() {
            this.previewLoaded = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.spinner {
    width: 8rem !important;
    height: 8rem !important;
}

.spinner-wrapper {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}
</style>
